import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Button,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  CircularProgress,
  Pagination,
  Modal,
  Stack,
  Tooltip,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Drawer,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import { ArrowDownward, ArrowUpward, Refresh } from "@mui/icons-material";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { styled } from "@mui/material/styles";
import { url } from "../Components/url";
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { useListViewMode } from "../hooks/useListPreferences";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { GlobalSearchBar } from "../Components/ERPList";
import styles from "./AllEnquiries.module.scss";
import { formatDateTimeIST } from "../utils/datetimeIst";

const LIST_ID = "exhibition-enquiries";
const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "default",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": { transform: "translateY(-4px)", boxShadow: theme.shadows[10] },
}));

const theme = createTheme({
  palette: { primary: { main: "#5e72e4" }, background: { default: "#f8f9fe" } },
  typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", h4: { fontWeight: 600 }, button: { textTransform: "none" } },
  shape: { borderRadius: 12 },
});

const StyledTableHead = styled(TableHead)(() => ({
  backgroundColor: "#f9fafc",
  "& th": {
    fontWeight: 700,
    fontSize: "14px",
    padding: "14px 8px",
    color: "#1a1f36",
    cursor: "pointer",
  },
}));

export default function ExhibitionEnquiries() {
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const limit = 25;
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedEnquiryId, setSelectedEnquiryId] = useState(null);
  const [counsellors, setCounsellors] = useState([]);
  const [selectedCounsellor, setSelectedCounsellor] = useState("");
  const [convertLoading, setConvertLoading] = useState(false);
  const [viewMode, setViewMode] = useListViewMode(LIST_ID, "table");
  const toggleViewMode = () => setViewMode((prev) => (prev === "cards" ? "table" : "cards"));

  useEffect(() => {
    fetchEnquiries();
  }, [page, limit, debouncedSearch, sortField, sortOrder]);

  useEffect(() => {
    fetchCounsellors();
  }, []);

  const fetchCounsellors = async () => {
    try {
      const res = await fetchWithAuth(`${url}/get_counsellors`);
      const data = await res.json();
      setCounsellors(data || []);
    } catch (error) {
      console.error("Error fetching counsellors:", error);
    }
  };

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(
        `${url}/exhibition-enquiries?page=${page}&limit=${limit}&search=${debouncedSearch}&sortField=${sortField}&sortOrder=${sortOrder}`
      );
      const data = await res.json();
      setEnquiries(data.data || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const openConvertModal = (id) => {
    setSelectedEnquiryId(id);
    setShowConvertModal(true);
  };

  const handleConvert = async () => {
    if (!selectedCounsellor) {
      alert("Please select a counsellor");
      return;
    }
    try {
      setConvertLoading(true);
      const res = await fetchWithAuth(`${url}/convert-exhibition-enquiry`, {
        method: "GET",
        body: JSON.stringify({
          exhibition_enquiry_id: selectedEnquiryId,
          application_counsellor_id: selectedCounsellor,
        }),
      });
      const data = await res.json();
      setConvertLoading(false);
      if (data.message) {
        alert("Converted successfully!");
        setShowConvertModal(false);
        setSelectedCounsellor("");
        fetchEnquiries();
      } else {
        alert("Error: " + data.error);
      }
    } catch (error) {
      alert("Failed to convert enquiry");
      setConvertLoading(false);
    }
  };

  const columns = [
    { key: "first_name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone_number", label: "Phone" },
    { key: "source", label: "Source" },
    { key: "created_at", label: "Created" },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
              <Box sx={{ marginBottom: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box>
                    <Typography variant="h4" className={styles.pageTitle}>
                      Exhibition Enquiries
                    </Typography>
                    <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                      Enquiries from exhibitions and events.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Box className={styles.header}>
                <GlobalSearchBar
                  value={search}
                  onChange={(v) => {
                    setSearch(v);
                    setPage(1);
                  }}
                  placeholder="Search name, email, phone..."
                />
                <Box className={styles.headerActions}>
                  <Tooltip title={viewMode === "cards" ? "Table View" : "Card View"}>
                    <IconButton onClick={toggleViewMode} color="primary">
                      {viewMode === "cards" ? <ViewListIcon /> : <ViewModuleIcon />}
                    </IconButton>
                  </Tooltip>
                  <Button variant="contained" startIcon={<Refresh />} onClick={() => fetchEnquiries()}>
                    Refresh
                  </Button>
                </Box>
              </Box>

              <Paper variant="outlined" className={styles.tableContainer}>
                {loading ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : viewMode === "cards" && enquiries.length > 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={2} className={styles.cardsGrid}>
                      {enquiries.map((q, i) => (
                        <Grid item xs={12} sm={6} md={4} key={q.id ?? i}>
                          <StyledCard>
                            <CardHeader
                              avatar={
                                <Avatar sx={{ bgcolor: "primary.main" }}>
                                  {q.first_name?.charAt(0) || "E"}
                                </Avatar>
                              }
                              title={`${q.first_name || ""} ${q.last_name || ""}`}
                              subheader={q.email}
                              className={styles.cardHeader}
                            />
                            <CardContent className={styles.cardContent}>
                              <Typography variant="body2">
                                <strong>Phone:</strong> {q.phone_number || "—"}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Source:</strong> {q.source || "—"}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Created:</strong> {q.time_created ? formatDateTimeIST(q.time_created) : "—"}
                              </Typography>
                              {q.converted ? (
                                <Typography color="success.main" fontWeight={500} sx={{ mt: 1 }}>
                                  Converted
                                </Typography>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  sx={{ mt: 1 }}
                                  onClick={() => openConvertModal(q.id)}
                                >
                                  Convert
                                </Button>
                              )}
                            </CardContent>
                          </StyledCard>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ) : viewMode === "cards" && enquiries.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography color="text.secondary">No enquiries found</Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table stickyHeader>
                      <StyledTableHead>
                        <TableRow>
                          {columns.map((col) => (
                            <TableCell key={col.key} onClick={() => handleSort(col.key)}>
                              {col.label}
                              {sortField === col.key
                                ? sortOrder === "asc"
                                  ? <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} />
                                  : <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                : null}
                            </TableCell>
                          ))}
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </StyledTableHead>
                      <TableBody>
                        {enquiries.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              No enquiries found
                            </TableCell>
                          </TableRow>
                        ) : (
                          enquiries.map((q, i) => (
                            <TableRow key={i} hover className={styles.tableRow}>
                              <TableCell className={styles.primaryText}>
                                {q.first_name} {q.last_name}
                              </TableCell>
                              <TableCell className={styles.secondaryText}>{q.email}</TableCell>
                              <TableCell>{q.phone_number}</TableCell>
                              <TableCell>{q.source}</TableCell>
                              <TableCell>{formatDateTimeIST(q.time_created)}</TableCell>
                              <TableCell>
                                {q.converted ? (
                                  <Typography color="success.main" fontWeight={500}>
                                    Converted
                                  </Typography>
                                ) : (
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    onClick={() => openConvertModal(q.id)}
                                  >
                                    Convert
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>

              <Box className={styles.pagination}>
                <Typography variant="body2" color="text.secondary">
                  Showing {enquiries.length} of {total} results
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </Paper>

        <Modal open={showConvertModal} onClose={() => setShowConvertModal(false)}>
          <Box
            sx={{
              width: 400,
              p: 4,
              bgcolor: "white",
              borderRadius: 3,
              boxShadow: 24,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              position: "absolute",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Convert Enquiry
            </Typography>
            <Select
              fullWidth
              value={selectedCounsellor}
              onChange={(e) => setSelectedCounsellor(e.target.value)}
              displayEmpty
              size="small"
            >
              <MenuItem value="">Select counsellor</MenuItem>
              {counsellors.map((c, index) => (
                <MenuItem key={index} value={typeof c === "object" ? c.id : c}>
                  {typeof c === "object" ? (c.username || c.name) : c}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 3 }}
              disabled={convertLoading}
              onClick={handleConvert}
            >
              {convertLoading ? "Converting…" : "Convert Enquiry"}
            </Button>
          </Box>
        </Modal>
      </Box>
    </ThemeProvider>
  );
}
